import type { LineData, UTCTimestamp } from 'lightweight-charts';
import type { DetailedSpreadDataPoint } from '@/services/spreadApi';

export type DetailedSpreadViewMode = 'orderbook' | 'mid';

export interface DetailedSpreadChartLine {
  key: string;
  label: string;
  color: string;
  data: LineData<UTCTimestamp>[];
}

const toUtcTimestamp = (timestamp: string): UTCTimestamp | null => {
  const ms = Date.parse(timestamp);
  if (!Number.isFinite(ms)) return null;
  return Math.floor(ms / 1000) as UTCTimestamp;
};

const sortByTime = <T extends { time: UTCTimestamp }>(data: T[]): T[] =>
  [...data].sort((left, right) => left.time - right.time);

export const mapDetailedSpreadLine = (
  data: DetailedSpreadDataPoint[],
  field: keyof DetailedSpreadDataPoint,
): LineData<UTCTimestamp>[] =>
  sortByTime(
    data
      .map((point): LineData<UTCTimestamp> | null => {
        const time = toUtcTimestamp(point.timestamp);
        const value = point[field];
        if (time == null || typeof value !== 'number' || !Number.isFinite(value)) return null;
        return { time, value: value * 100 };
      })
      .filter((point): point is LineData<UTCTimestamp> => point != null),
  );

const line = (
  rows: DetailedSpreadDataPoint[],
  key: keyof DetailedSpreadDataPoint,
  label: string,
  color: string,
): DetailedSpreadChartLine => ({
  key: String(key),
  label,
  color,
  data: mapDetailedSpreadLine(rows, key),
});

export function buildForwardDetailedSpreadLines(
  rows: DetailedSpreadDataPoint[],
  mode: DetailedSpreadViewMode,
  colors: { primary: string; secondary: string },
): DetailedSpreadChartLine[] {
  if (mode === 'mid') {
    return [
      line(rows, 'long1_short2_mid_spread', 'long1_short2_mid_spread %', colors.primary),
      line(rows, 'median_long1_short2_mid_spread', 'median_long1_short2_mid_spread %', colors.secondary),
    ];
  }

  return [
    line(rows, 'long1_short2_close_spread', 'long1_short2_close_spread %', colors.primary),
    line(rows, 'long1_short2_open_spread', 'long1_short2_open_spread %', '#7c3aed'),
    line(rows, 'median_long1_short2_close_spread', 'median_long1_short2_close_spread %', colors.secondary),
    line(rows, 'long1_short2_open_line', 'long1_short2_open_line %', '#16a34a'),
    line(rows, 'long1_short2_close_line', 'long1_short2_close_line %', '#dc2626'),
  ];
}

export function buildReverseDetailedSpreadLines(
  rows: DetailedSpreadDataPoint[],
  mode: DetailedSpreadViewMode,
  colors: { primary: string; secondary: string },
): DetailedSpreadChartLine[] {
  if (mode === 'mid') {
    return [
      line(rows, 'short1_long2_mid_spread', 'short1_long2_mid_spread %', colors.primary),
      line(rows, 'median_short1_long2_mid_spread', 'median_short1_long2_mid_spread %', colors.secondary),
    ];
  }

  return [
    line(rows, 'short1_long2_close_spread', 'short1_long2_close_spread %', colors.primary),
    line(rows, 'short1_long2_open_spread', 'short1_long2_open_spread %', '#7c3aed'),
    line(rows, 'median_short1_long2_close_spread', 'median_short1_long2_close_spread %', colors.secondary),
    line(rows, 'short1_long2_open_line', 'short1_long2_open_line %', '#16a34a'),
    line(rows, 'short1_long2_close_line', 'short1_long2_close_line %', '#dc2626'),
  ];
}

export function detailedSpreadViewModeLabel(mode: DetailedSpreadViewMode): string {
  return mode === 'orderbook' ? 'Mid 价差' : 'Open/Close';
}
